/**
 * NetworkGraph — elemento de assinatura visual da sepolTECH.
 *
 * Muitas trilhas de circuito convergindo para uma área central de
 * "chip", com uma dobra diagonal de 45° e depois um trecho reto —
 * o mesmo padrão de roteamento visto em placas de circuito reais.
 * Cada trilha tem um pulso de luz percorrendo o trajeto.
 *
 * As trilhas são geradas por fórmula (não digitadas uma a uma) pra
 * ficar fácil ajustar a densidade — troque HERO_TRACE_COUNT /
 * BAND_TRACE_COUNT abaixo pra mais ou menos linhas.
 *
 * Uso:
 *   <NetworkGraph variant="hero" />   // dentro de .hero
 *   <NetworkGraph variant="footer" /> // dentro de .footer ou .page-hero
 */

// Roteamento "chanfrado": uma diagonal de 45° seguida de um trecho
// reto — é o visual clássico de trilha de PCB.
function chamferRoute(x1, y1, x2, y2) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const d = Math.min(Math.abs(dx), Math.abs(dy));
  const midX = x1 + Math.sign(dx) * d;
  const midY = y1 + Math.sign(dy) * d;
  return [[x1, y1], [midX, midY], [x2, y2]];
}

// Pequeno jitter determinístico (sem Math.random) só pra tirar a
// rigidez de uma grade perfeitamente uniforme.
function jitter(i, spread = 18) {
  return ((i * 53) % 37) / 37 * spread - spread / 2;
}

function buildPath(points) {
  return points
    .map(([x, y], i) => `${i === 0 ? "M" : "L"}${x},${y}`)
    .join(" ");
}

function Trace({ points, duration, delay = 0 }) {
  const d = buildPath(points);
  return (
    <>
      <path className="network-graph__trace" d={d} />
      <path
        className="network-graph__pulse"
        d={d}
        style={{ animationDuration: `${duration}s`, animationDelay: `${delay}s` }}
      />
      {points.map(([x, y], i) => (
        <circle key={i} className="network-graph__pad" cx={x} cy={y} r={2.6} />
      ))}
    </>
  );
}

/**
 * Gera um conjunto de trilhas convergindo para um "chip" central,
 * a partir de três lados (esquerda, direita, e algumas soltas vindo
 * do topo cruzando o quadro pro fundo, só de textura).
 */
function generateTraces({ width, height, chip, fromLeft, fromRight, loose }) {
  const traces = [];
  let i = 0;

  // Trilhas vindas da esquerda, convergindo pro lado esquerdo do chip
  for (let n = 0; n < fromLeft; n++, i++) {
    const startY = 24 + (n * (height - 48)) / (fromLeft - 1) + jitter(i);
    const endY = chip.y - chip.h / 2 + (n * chip.h) / (fromLeft - 1);
    const [p1, p2, p3] = chamferRoute(0, startY, chip.x - chip.w / 2, endY);
    // Trilhas vindas da esquerda — duration bem mais lenta
    traces.push({
      points: [p1, p2, p3],
      duration: 11 + (i % 6) * 1.5,
      delay: -((i * 0.7) % 5),
    });
  }

  // Trilhas vindas da direita, convergindo pro lado direito do chip
  for (let n = 0; n < fromRight; n++, i++) {
    const startY = 30 + (n * (height - 60)) / (fromRight - 1) + jitter(i);
    const endY = chip.y - chip.h / 2 + (n * chip.h) / (fromRight - 1);
    const [p1, p2, p3] = chamferRoute(width, startY, chip.x + chip.w / 2, endY);
    traces.push({
      points: [p1, p2, p3],
      duration: 12 + (i % 5) * 1.5,
      delay: -((i * 0.9) % 5),
    });
  }

  // Trilhas soltas — cruzam o quadro na diagonal, sem ligar ao chip,
  // só pra dar densidade de fundo (como no canto esquerdo da referência)
  for (let n = 0; n < loose; n++, i++) {
    const startX = (n * width) / loose + jitter(i, 40);
    const [p1, p2, p3] = chamferRoute(startX, 0, startX - width * 0.09, height);
    traces.push({
      points: [p1, p2, p3],
      duration: 13 + (i % 4) * 1.2,
      delay: -((i * 0.6) % 5),
    });
  }

  return traces;
}

const HERO_TRACES = generateTraces({
  width: 1600,
  height: 900,
  chip: { x: 1150, y: 460, w: 180, h: 160 },
  fromLeft: 8,
  fromRight: 5,
  loose: 3,
});

const BAND_TRACES = generateTraces({
  width: 1600,
  height: 420,
  chip: { x: 1150, y: 210, w: 160, h: 100 },
  fromLeft: 6,
  fromRight: 3,
  loose: 2,
});

export default function NetworkGraph({ variant = "hero", className = "" }) {
  if (variant === "footer") {
    return (
      <svg
        className={`footer__network ${className}`}
        viewBox="0 0 1600 420"
        preserveAspectRatio="xMidYMid slice"
        aria-hidden="true"
        focusable="false"
      >
        {BAND_TRACES.map((t, i) => (
          <Trace key={i} {...t} />
        ))}
      </svg>
    );
  }

  // variant === "hero"
  return (
    <svg
      className={`hero__network ${className}`}
      viewBox="0 0 1600 900"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
      focusable="false"
    >
      {HERO_TRACES.map((t, i) => (
        <Trace key={i} {...t} />
      ))}
    </svg>
  );
}
